import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Link2, Image as ImageIcon, Minus, Undo2, Redo2,
  Code, Pilcrow,
} from "lucide-react";

/**
 * WordPress-style WYSIWYG editor built on TipTap.
 * Emits HTML via onChange whenever the doc changes.
 */
const RichTextEditor = ({ value = "", onChange, placeholder = "Write your page content..." }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener", target: "_blank" } }),
      Image.configure({ inline: false, HTMLAttributes: { class: "cms-image" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose max-w-none min-h-[420px] focus:outline-none p-6 text-[#0A192F] leading-relaxed",
        "data-testid": "rte-editor",
      },
    },
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
  });

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Image URL (paste from Media Library or external)");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  if (!editor) return null;

  const btn = (active, action, testId, children, title) => (
    <button
      type="button"
      onClick={action}
      data-testid={testId}
      title={title}
      className={`p-2 border transition-colors ${
        active
          ? "border-[#D4AF37] bg-[#FFF9E6] text-[#0A192F]"
          : "border-gray-300 text-[#0A192F] hover:border-[#D4AF37] hover:text-[#D4AF37]"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-300" data-testid="rich-text-editor">
      <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50">
        {btn(editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), "rte-h1", <Heading1 size={14} />, "H1")}
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "rte-h2", <Heading2 size={14} />, "H2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), "rte-h3", <Heading3 size={14} />, "H3")}
        {btn(editor.isActive("paragraph"), () => editor.chain().focus().setParagraph().run(), "rte-p", <Pilcrow size={14} />, "Paragraph")}
        <Sep />
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "rte-bold", <Bold size={14} />, "Bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "rte-italic", <Italic size={14} />, "Italic")}
        {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), "rte-underline", <UnderlineIcon size={14} />, "Underline")}
        <Sep />
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "rte-ul", <List size={14} />, "Bullet list")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "rte-ol", <ListOrdered size={14} />, "Numbered list")}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), "rte-quote", <Quote size={14} />, "Quote")}
        {btn(editor.isActive("code"), () => editor.chain().focus().toggleCode().run(), "rte-code", <Code size={14} />, "Inline code")}
        <Sep />
        {btn(editor.isActive("link"), addLink, "rte-link", <Link2 size={14} />, "Insert link")}
        {btn(false, addImage, "rte-image", <ImageIcon size={14} />, "Insert image")}
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), "rte-hr", <Minus size={14} />, "Horizontal rule")}
        <Sep />
        {btn(false, () => editor.chain().focus().undo().run(), "rte-undo", <Undo2 size={14} />, "Undo")}
        {btn(false, () => editor.chain().focus().redo().run(), "rte-redo", <Redo2 size={14} />, "Redo")}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

const Sep = () => <div className="w-px bg-gray-300 mx-1 self-stretch" />;

export default RichTextEditor;
